/**
 * OrderContactsModal — "Who gets this order's emails" dialog.
 *
 * Owner feedback (review rva86r, 9/2): CSRs must be able to say, per person,
 * whether they get the order confirmation, the invoice, or both — and the
 * pop-out "looked awful". This is a proper dialog on the app's shared
 * <Modal>:
 *
 *   Recipient                         Confirmation   Invoice
 *   ─ From the sales site (locked) ─────────────────────────
 *   wcconfirmpo@…  Sales-site list        ✓             –        (not editable)
 *   Marty · vendorinvoices@…  billing     –             ✓
 *   ─ This order ────────────────────────────────────────────
 *   Kevin · branch.nh@…  Sage ship-to    [x]           [x]       ×
 *   ─ Add a recipient ──────────────────────────────────────
 *   [search directory…]  + New contact (remembered for ship-to 011)
 *
 * Edits are local until **Save** (one PUT); Cancel discards.
 *
 * Recorder (kanban 1790c14f): every per-person control names its FAMILY via
 * data-action and carries the contact id in data-target-contact-id, never the
 * person. Without this the shared library named the pick by its label
 * ("button_chris_kreuzer_ckreuzer_carrollsupply_com"), the × by
 * "button_remove_<name>" and the checkboxes by their aria-label, so contact
 * names and addresses landed in ui_action_events.action. The checkbox rows
 * carry the flag's NEW state (`confirmation` / `invoice` = on|off): the flag
 * is set synchronously in onChange, so React has re-rendered the controlled
 * box before the native change event reaches the library (unlike a box whose
 * state only changes after an awaited request, which reports its old state).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../common/Modal';
import { orderNotificationService } from '../../services/orderNotifications';
import type {
  NotificationContact,
  OrderContactCandidate,
  SalesSiteRecipient,
} from '../../types/orderNotifications';
import styles from './OrderContactsModal.module.css';
import {
  apiErrorMessage as errorMessage,
  candidateToContact,
  existingContactIdFromError,
  filterCandidates,
  looksLikeEmail,
} from './orderContactsUtils';

export interface OrderContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  customerNo: string | null;
  shipToCode?: string | null;
  contacts: NotificationContact[];
  onSaved: (contacts: NotificationContact[]) => void;
  service?: Pick<
    typeof orderNotificationService,
    'listCustomerContacts' | 'createCustomerContact' | 'setOrderContacts'
  >;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const OrderContactsModal: React.FC<OrderContactsModalProps> = ({
  isOpen,
  onClose,
  orderId,
  customerNo,
  shipToCode,
  contacts,
  onSaved,
  service = orderNotificationService,
}) => {
  const [draft, setDraft] = useState<NotificationContact[]>(contacts);
  const [candidates, setCandidates] = useState<OrderContactCandidate[]>([]);
  const [fixed, setFixed] = useState<SalesSiteRecipient[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newConf, setNewConf] = useState(true);
  const [newInv, setNewInv] = useState(true);
  const [creating, setCreating] = useState(false);

  // The working copy is initialised from `contacts` on mount; the row remounts
  // this dialog (React `key`) every time it opens, so each opening starts fresh.

  const loadDirectory = useCallback(async () => {
    if (!customerNo) {
      setCandidates([]);
      setFixed([]);
      return;
    }
    setLoading(true);
    try {
      const resp = await service.listCustomerContacts(customerNo, orderId);
      setCandidates(resp.contacts);
      setFixed(resp.sales_site_recipients ?? []);
    } catch (err) {
      setError(errorMessage(err, 'Could not load contacts for this customer.'));
    } finally {
      setLoading(false);
    }
  }, [customerNo, orderId, service]);

  useEffect(() => {
    if (isOpen) void loadDirectory();
  }, [isOpen, loadDirectory]);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(contacts),
    [draft, contacts],
  );

  const setFlag = (email: string, flag: 'confirmation' | 'invoice', value: boolean) =>
    setDraft((d) => d.map((c) => (c.email === email ? { ...c, [flag]: value } : c)));

  const remove = (email: string) => setDraft((d) => d.filter((c) => c.email !== email));

  const add = (contact: NotificationContact) => {
    setDraft((d) => (d.some((c) => c.email === contact.email) ? d : [...d, contact]));
    setQuery('');
  };

  const { suggested, others } = useMemo(
    () => filterCandidates(candidates, draft, query),
    [candidates, draft, query],
  );
  // Nobody is flagged for this order (no PO sender, nobody used before, nothing
  // saved for this ship-to): say so, and say where the buyer can still be found
  // (kanban 7479fb71). Suggestions that are merely hidden because they were
  // already picked don't count.
  const noSuggestions = !loading && !query.trim() && !candidates.some((c) => c.suggested);

  const submitNewContact = async () => {
    const name = newName.trim();
    const email = newEmail.trim().toLowerCase();
    if (!name) return setError('Enter the buyer’s name.');
    if (!EMAIL_RE.test(email)) return setError('Enter a valid email address.');
    if (!customerNo) return setError('Pick a customer on the order first.');
    setCreating(true);
    setError(null);
    try {
      const created = await service.createCustomerContact(customerNo, {
        name,
        email,
        ship_to_code: shipToCode || null,
        notify_confirmation: newConf,
        notify_invoice: newInv,
      });
      add({ name: created.name, email: created.email, source: 'portal', contact_id: created.id, confirmation: newConf, invoice: newInv });
      setNewName('');
      setNewEmail('');
      setShowNewForm(false);
      void loadDirectory();
    } catch (err) {
      const existingId = existingContactIdFromError(err);
      if (existingId) {
        add({ name, email, source: 'portal', contact_id: existingId, confirmation: newConf, invoice: newInv });
        setShowNewForm(false);
      } else {
        setError(errorMessage(err, 'Could not add the contact.'));
      }
    } finally {
      setCreating(false);
    }
  };

  // "+ New contact" after a search that found nobody: carry the typed text
  // into the right field. An address goes to Email, a name to Name — before
  // (kanban 9479e4de) an email always landed in Name.
  const openNewForm = () => {
    setShowNewForm(true);
    const typed = query.trim();
    if (!typed) return;
    if (looksLikeEmail(typed)) {
      if (!newEmail) setNewEmail(typed.toLowerCase());
    } else if (!newName) {
      setNewName(typed);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const resp = await service.setOrderContacts(orderId, draft);
      onSaved(resp.contacts);
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Could not save contacts.'));
    } finally {
      setSaving(false);
    }
  };

  const renderCandidate = (c: OrderContactCandidate) => (
    <button
      key={`${c.email}-${c.source}`}
      type="button"
      className={styles.candidate}
      onClick={() => add(candidateToContact(c))}
      data-testid="order-contact-option"
      data-action="order_contact_pick"
      data-target-order-id={orderId}
      data-target-contact-id={c.contact_id ?? undefined}
      data-record-value="source"
      value={c.source}
    >
      <span className={styles.candidateMain}>
        <span className={styles.name}>
          {c.name}
          {c.suggested && c.suggested_reason && (
            <span className={styles.reason} data-testid="order-contact-reason">{c.suggested_reason}</span>
          )}
        </span>
        <span className={styles.email}>{c.email}</span>
      </span>
      <span className={styles.badge}>{c.label}</span>
    </button>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Who gets this order’s emails" size="lg">
      <div className={styles.wrap} data-testid="order-contacts-modal">
        <p className={styles.intro}>
          The <b>order confirmation</b> goes out when the order is approved in Sage; the <b>invoice</b>
          (with the PDF) once it ships and is invoiced. Both are Adam’s emails relayed as-is. The sales
          site mails the account’s order list (confirmation) and the Sage AR email (invoice) on its own;
          those rows are locked. Everyone else is per order.
        </p>

        <table className={styles.table}>
          <thead>
            <tr>
              <th>Recipient</th>
              <th className={styles.center}>Confirmation</th>
              <th className={styles.center}>Invoice</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {fixed.length > 0 && (
              <tr className={styles.groupRow}>
                <td colSpan={4}>
                  Already mailed by the sales site — always included
                  <span className={styles.lockedNote} data-testid="order-contacts-fixed-why">
                    {' '}These come from the customer’s set-up in Sage, so they can’t be unchecked here;
                    to change them, update the customer in Sage.
                  </span>
                </td>
              </tr>
            )}
            {fixed.map((f) => (
              <tr key={`fixed-${f.email}`} className={styles.fixedRow} data-testid="order-contacts-fixed-row">
                <td>
                  <span className={styles.name}>{f.name}</span>
                  <span className={styles.email}>{f.email}</span>
                </td>
                <td className={styles.center}>{f.kind === 'confirmation' ? <span className={styles.lockedTick}>✓</span> : <span className={styles.dash}>–</span>}</td>
                <td className={styles.center}>{f.kind === 'invoice' ? <span className={styles.lockedTick}>✓</span> : <span className={styles.dash}>–</span>}</td>
                <td className={styles.center}>
                  <span className={styles.lockedNote} data-testid="order-contacts-fixed-source">
                    {f.kind === 'confirmation' ? 'sales-site order list' : 'Sage AR email'}
                  </span>
                </td>
              </tr>
            ))}
            <tr className={styles.groupRow}>
              <td colSpan={4}>This order</td>
            </tr>
            {draft.length === 0 && (
              <tr>
                <td colSpan={4} className={styles.emptyRow}>No extra recipient yet — add one below.</td>
              </tr>
            )}
            {draft.map((c) => (
              <tr key={c.email} data-testid="order-contact-chip">
                <td>
                  <span className={styles.name}>{c.name}</span>
                  <span className={styles.email}>{c.email}</span>
                </td>
                <td className={styles.center}>
                  <input
                    type="checkbox"
                    checked={c.confirmation}
                    onChange={(e) => setFlag(c.email, 'confirmation', e.target.checked)}
                    aria-label={`${c.name} gets the confirmation`}
                    data-action="order_contact_confirmation_toggle"
                    data-target-order-id={orderId}
                    data-target-contact-id={c.contact_id ?? undefined}
                    data-record-value="confirmation"
                  />
                </td>
                <td className={styles.center}>
                  <input
                    type="checkbox"
                    checked={c.invoice}
                    onChange={(e) => setFlag(c.email, 'invoice', e.target.checked)}
                    aria-label={`${c.name} gets the invoice`}
                    data-action="order_contact_invoice_toggle"
                    data-target-order-id={orderId}
                    data-target-contact-id={c.contact_id ?? undefined}
                    data-record-value="invoice"
                  />
                </td>
                <td className={styles.center}>
                  <button
                    type="button"
                    className={styles.remove}
                    aria-label={`Remove ${c.name}`}
                    title="Remove from this order"
                    onClick={() => remove(c.email)}
                    data-action="order_contact_remove"
                    data-target-order-id={orderId}
                    data-target-contact-id={c.contact_id ?? undefined}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={styles.addSection}>
          <div className={styles.addHeader}>Add a recipient</div>
          <input
            className={styles.search}
            placeholder="Search the customer’s contacts by name or email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="order-contacts-search"
          />
          {noSuggestions && (
            <div className={styles.muted} data-testid="order-contacts-no-suggested">
              {shipToCode
                ? `No suggested buyer for ship-to ${shipToCode} yet. Search below or add a new contact; they’ll be remembered for ship-to ${shipToCode}.`
                : 'No suggested buyer for this customer yet. Search below or add a new contact.'}
            </div>
          )}
          <div className={styles.candidates}>
            {loading && <div className={styles.muted}>Loading contacts…</div>}
            {!loading && suggested.length > 0 && (
              <>
                <div className={styles.groupLabel}>Suggested</div>
                {suggested.map(renderCandidate)}
              </>
            )}
            {!loading && others.length > 0 && (
              <>
                <div className={styles.groupLabel}>{suggested.length > 0 ? 'All contacts' : 'Contacts'}</div>
                {others.map(renderCandidate)}
              </>
            )}
            {!loading && suggested.length === 0 && others.length === 0 && (
              <div className={styles.muted}>{query ? 'No matching contacts.' : 'No more contacts on file for this customer.'}</div>
            )}
          </div>
          {!showNewForm ? (
            <button
              type="button"
              className={styles.link}
              onClick={openNewForm}
              data-testid="order-contacts-new"
              data-action="order_contact_new_open"
              data-target-order-id={orderId}
            >
              + New contact…{shipToCode ? ` (remembered for ship-to ${shipToCode})` : ''}
            </button>
          ) : (
            <div className={styles.newForm} data-testid="order-contacts-new-form">
              <input placeholder="Name" value={newName} onChange={(e) => setNewName(e.target.value)} aria-label="New contact name" />
              <input placeholder="Email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} aria-label="New contact email"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submitNewContact(); } }} />
              <label className={styles.check}><input type="checkbox" checked={newConf} onChange={(e) => setNewConf(e.target.checked)} aria-label="New contact gets the confirmation" /> Confirmation</label>
              <label className={styles.check}><input type="checkbox" checked={newInv} onChange={(e) => setNewInv(e.target.checked)} aria-label="New contact gets the invoice" /> Invoice</label>
              <button
                type="button"
                className={styles.primary}
                onClick={() => void submitNewContact()}
                disabled={creating}
                data-testid="order-contacts-new-save"
                data-action="order_contact_create"
                data-target-order-id={orderId}
                data-outcome="request"
              >
                {creating ? 'Adding…' : 'Add'}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className={styles.error} role="alert">{error}</div>
        )}

        <div className={styles.footer}>
          <button type="button" className={styles.secondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className={styles.primary}
            onClick={() => void save()}
            disabled={saving || !dirty}
            data-testid="order-contacts-save"
            data-action="order_contacts_save"
            data-target-order-id={orderId}
            data-outcome="request"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default OrderContactsModal;
